import React, { useState } from 'react';
import {
  Eye, EyeOff, MapPin, Activity, Camera, Gauge, Clock,
  AlertTriangle, X, Info, User, Car,
  ToggleLeft, ToggleRight, Save
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormat } from '../../i18n/format.js';
import { apiRequest } from '../../api/core.js';

// Codes des données désactivables : les libellés sont résolus au rendu par t().
const DISABLE_OPTIONS = [
  { key: 'disable_gps_tracking', labelKey: 'compliance.privacyMode.form.items.gpsPosition', icon: MapPin },
  { key: 'disable_behavior_events', labelKey: 'compliance.privacyMode.form.items.behavior', icon: Activity },
  { key: 'disable_speed_alerts', labelKey: 'compliance.privacyMode.form.items.speedAlerts', icon: Gauge },
  { key: 'disable_camera', labelKey: 'compliance.privacyMode.form.items.camera', icon: Camera },
];

function PrivacyModePanel({ onMessage }) {
  const { t, fmt } = useFormat();
  const queryClient = useQueryClient();
  const [, setSelectedDriver] = useState(null); // Reserved for driver detail modal
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    driver_id: '',
    vehicle_id: '',
    privacy_mode_enabled: false,
    reason: '',
    disable_gps_tracking: true,
    disable_behavior_events: true,
    disable_speed_alerts: true,
    disable_camera: true,
    work_hours_only: false,
    work_start_time: '08:00',
    work_end_time: '18:00'
  });

  // Fetch drivers
  const { data: driversData } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => apiRequest('/driver')
  });

  // Fetch vehicles
  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => apiRequest('/vehicle')
  });

  // Fetch privacy mode settings (tenant-isolated via middleware)
  const { data: privacySettings, isLoading } = useQuery({
    queryKey: ['privacy_mode'],
    queryFn: () => apiRequest('/gdpr-compliance/privacy-mode')
  });

  // Create privacy mode setting (org_id injected from JWT by middleware)
  const createMutation = useMutation({
    mutationFn: (data) => apiRequest('/gdpr-compliance/privacy-mode', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['privacy_mode']);
      setShowForm(false);
      onMessage?.({ type: 'success', text: t('compliance.privacyMode.toast.created') });
    }
  });

  // Update privacy mode setting (ownership verified by middleware)
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => apiRequest(`/gdpr-compliance/privacy-mode/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['privacy_mode']);
      onMessage?.({ type: 'success', text: t('compliance.privacyMode.toast.updated') });
    }
  });

  const drivers = driversData?.data || [];
  const vehicles = vehiclesData?.data || [];
  const settings = privacySettings?.data || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const togglePrivacyMode = (setting) => {
    updateMutation.mutate({
      id: setting.id,
      data: { privacy_mode_enabled: !setting.privacy_mode_enabled }
    });
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? `${driver.first_name} ${driver.last_name}` : t('common.states.notAvailable');
  };

  const getVehicleName = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})` : t('common.states.notAvailable');
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-800">{t('compliance.privacyMode.info.title')}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('compliance.privacyMode.info.body')}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Toggle Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <Eye className="w-5 h-5 mr-2 text-purple-600" />
            {t('compliance.privacyMode.quickToggle.title')}
          </h3>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            + {t('compliance.privacyMode.quickToggle.newSetting')}
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">{t('common.states.loading')}</div>
        ) : settings.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <EyeOff className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{t('compliance.privacyMode.empty.title')}</p>
            <p className="text-sm mt-1">{t('compliance.privacyMode.empty.hint')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {settings.map(setting => (
              <PrivacyModeCard
                key={setting.id}
                t={t}
                fmt={fmt}
                setting={setting}
                driverName={getDriverName(setting.driver_id)}
                vehicleName={getVehicleName(setting.vehicle_id)}
                onToggle={() => togglePrivacyMode(setting)}
              />
            ))}
          </div>
        )}
      </div>

      {/* What Gets Disabled */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">{t('compliance.privacyMode.disabledData.title')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <DataTypeCard
            icon={MapPin}
            title={t('compliance.privacyMode.disabledData.gpsPosition.title')}
            description={t('compliance.privacyMode.disabledData.gpsPosition.description')}
            color="red"
          />
          <DataTypeCard
            icon={Activity}
            title={t('compliance.privacyMode.disabledData.behavior.title')}
            description={t('compliance.privacyMode.disabledData.behavior.description')}
            color="amber"
          />
          <DataTypeCard
            icon={Gauge}
            title={t('compliance.privacyMode.disabledData.speedAlerts.title')}
            description={t('compliance.privacyMode.disabledData.speedAlerts.description')}
            color="blue"
          />
          <DataTypeCard
            icon={Camera}
            title={t('compliance.privacyMode.disabledData.camera.title')}
            description={t('compliance.privacyMode.disabledData.camera.description')}
            color="purple"
          />
        </div>
      </div>

      {/* Work Hours Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-blue-600" />
          {t('compliance.privacyMode.workHours.title')}
        </h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-3">
            {t('compliance.privacyMode.workHours.body')}
          </p>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <span className="text-sm text-gray-700 mr-2">{t('compliance.privacyMode.workHours.start')}</span>
              <span className="font-mono bg-white px-3 py-1 rounded border">08:00</span>
            </div>
            <span className="text-gray-400">-</span>
            <div className="flex items-center">
              <span className="text-sm text-gray-700 mr-2">{t('compliance.privacyMode.workHours.end')}</span>
              <span className="font-mono bg-white px-3 py-1 rounded border">18:00</span>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-800">{t('compliance.privacyMode.legal.title')}</h4>
            <ul className="text-sm text-amber-700 mt-2 space-y-1">
              <li>- {t('compliance.privacyMode.legal.items.germany')}</li>
              <li>- {t('compliance.privacyMode.legal.items.france')}</li>
              <li>- {t('compliance.privacyMode.legal.items.obligations')}</li>
              <li>- {t('compliance.privacyMode.legal.items.legalBasis')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('compliance.privacyMode.form.title')}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.privacyMode.form.driver')}</label>
                <select
                  value={formData.driver_id}
                  onChange={e => setFormData({ ...formData, driver_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">{t('compliance.privacyMode.form.selectDriver')}</option>
                  {drivers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.privacyMode.form.vehicle')}</label>
                <select
                  value={formData.vehicle_id}
                  onChange={e => setFormData({ ...formData, vehicle_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">{t('compliance.privacyMode.form.allVehicles')}</option>
                  {vehicles.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium">{t('compliance.privacyMode.form.enabled')}</span>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, privacy_mode_enabled: !formData.privacy_mode_enabled })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.privacy_mode_enabled ? 'bg-purple-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    formData.privacy_mode_enabled ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.privacyMode.form.reason')}</label>
                <textarea
                  value={formData.reason}
                  onChange={e => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  rows="2"
                  placeholder={t('compliance.privacyMode.form.reasonPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">{t('compliance.privacyMode.form.dataToDisable')}</label>
                {DISABLE_OPTIONS.map(item => (
                  <label key={item.key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData[item.key]}
                      onChange={e => setFormData({ ...formData, [item.key]: e.target.checked })}
                      className="mr-2"
                    />
                    <item.icon className="w-4 h-4 mr-2 text-gray-500" />
                    {t(item.labelKey)}
                  </label>
                ))}
              </div>

              <div className="border-t pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Save className="w-4 h-4 inline mr-1" />
                  {t('common.actions.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function PrivacyModeCard({ t, fmt, setting, driverName, vehicleName, onToggle }) {
  return (
    <div className={`border rounded-lg p-4 ${setting.privacy_mode_enabled ? 'border-purple-300 bg-purple-50' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            setting.privacy_mode_enabled ? 'bg-purple-200 text-purple-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {setting.privacy_mode_enabled ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{driverName}</span>
            </div>
            {setting.vehicle_id && (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Car className="w-4 h-4" />
                <span>{vehicleName}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="text-right text-sm">
            <div className={setting.privacy_mode_enabled ? 'text-purple-700 font-medium' : 'text-gray-500'}>
              {setting.privacy_mode_enabled ? t('compliance.privacyMode.card.active') : t('compliance.privacyMode.card.inactive')}
            </div>
            {setting.enabled_at && setting.privacy_mode_enabled && (
              <div className="text-xs text-gray-400">
                {t('compliance.privacyMode.card.since', { date: fmt.dateTime(setting.enabled_at) })}
              </div>
            )}
          </div>
          <button
            onClick={onToggle}
            className={`p-2 rounded-lg transition-colors ${
              setting.privacy_mode_enabled
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {setting.privacy_mode_enabled ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Data Types Disabled */}
      {setting.privacy_mode_enabled && (
        <div className="mt-3 pt-3 border-t border-purple-200 flex flex-wrap gap-2">
          {setting.disable_gps_tracking && (
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center">
              <MapPin className="w-3 h-3 mr-1" /> {t('compliance.privacyMode.card.badges.gpsOff')}
            </span>
          )}
          {setting.disable_behavior_events && (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center">
              <Activity className="w-3 h-3 mr-1" /> {t('compliance.privacyMode.card.badges.behaviorOff')}
            </span>
          )}
          {setting.disable_speed_alerts && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center">
              <Gauge className="w-3 h-3 mr-1" /> {t('compliance.privacyMode.card.badges.alertsOff')}
            </span>
          )}
          {setting.disable_camera && (
            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center">
              <Camera className="w-3 h-3 mr-1" /> {t('compliance.privacyMode.card.badges.cameraOff')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function DataTypeCard({ icon: Icon, title, description, color }) {
  const colorClasses = {
    red: 'bg-red-50 border-red-200 text-red-600',
    amber: 'bg-amber-50 border-amber-200 text-amber-600',
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    purple: 'bg-purple-50 border-purple-200 text-purple-600'
  };

  return (
    <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
      <Icon className="w-8 h-8 mb-2" />
      <h4 className="font-medium text-gray-900">{title}</h4>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

export default PrivacyModePanel;
